import type { PageType, SiteType } from "../schema/index.js";
import type {
  CanonicalOutputContract,
  CanonicalOutputKey,
  ResolveOutputContractInput,
  ResolvedOutputContract,
} from "./types.js";

export {
  BLOCK_CAPABILITIES,
  BLOCK_OUTPUT_RESTRICTION_CODES,
  BLOCK_OUTPUT_SUPPORT_LEVELS,
  CANONICAL_OUTPUT_CAPABILITIES,
  CANONICAL_OUTPUT_CHANNELS,
  CANONICAL_OUTPUT_KEYS,
  CANONICAL_OUTPUT_STATES,
  OUTPUT_RESOLUTION_SOURCES,
} from "./types.js";

export type {
  BlockCapability,
  BlockOutputContract,
  BlockOutputRestrictionCode,
  BlockOutputSupport,
  BlockOutputSupportLevel,
  CanonicalOutputCapability,
  CanonicalOutputChannel,
  CanonicalOutputCompatibility,
  CanonicalOutputContract,
  CanonicalOutputKey,
  CanonicalOutputRestrictions,
  CanonicalOutputState,
  OutputResolutionSource,
  ResolveOutputContractInput,
  ResolvedOutputContract,
} from "./types.js";

const OUTPUT_CONTRACTS = [
  {
    key: "page",
    label: "Page",
    description: "Canonical web page output for standalone pages, websites, blogs, and link-in-bio surfaces.",
    channel: "web",
    state: "active",
    capabilities: [
      "analytics",
      "automation",
      "blog_content",
      "conversion_tracking",
      "custom_domain",
      "forms",
      "navigation",
      "seo",
      "site_shell",
      "theming",
    ],
    restrictions: {
      allowsAutomation: true,
      allowsCustomDomain: true,
      allowsForms: true,
      allowsIndexing: true,
      allowsNavigation: true,
      allowsSeo: true,
      allowsSiteShell: true,
      allowsTheming: true,
      supportsCourseProgress: false,
      supportsFunnelRouting: false,
      supportsVerification: false,
    },
    compatibility: {
      currentPageTypes: [
        "lead_capture",
        "webinar",
        "sales",
        "booking",
        "thank_you",
        "link_in_bio",
        "custom",
        "site_page",
        "blog_post",
      ],
      currentSiteTypes: [
        "website",
        "blog",
        "link_in_bio",
      ],
      defaultPageType: "custom",
      defaultSiteType: "website",
    },
  },
  {
    key: "funnel",
    label: "Funnel",
    description: "Step-based conversion flow output backed by funnel sites and funnel-step pages.",
    channel: "web",
    state: "active",
    capabilities: [
      "analytics",
      "automation",
      "conversion_tracking",
      "forms",
      "funnel_routing",
      "theming",
    ],
    restrictions: {
      allowsAutomation: true,
      allowsCustomDomain: true,
      allowsForms: true,
      allowsIndexing: false,
      allowsNavigation: false,
      allowsSeo: true,
      allowsSiteShell: false,
      allowsTheming: true,
      supportsCourseProgress: false,
      supportsFunnelRouting: true,
      supportsVerification: false,
    },
    compatibility: {
      currentPageTypes: ["funnel_step"],
      currentSiteTypes: ["funnel"],
      defaultPageType: "funnel_step",
      defaultSiteType: "funnel",
    },
  },
  {
    key: "email",
    label: "Email",
    description: "Email-mode output for client, campaign, and transactional sends. Contract is explicit but not yet persisted through page/site enums.",
    channel: "email",
    state: "planned",
    capabilities: [
      "analytics",
      "automation",
      "conversion_tracking",
      "forms",
      "theming",
    ],
    restrictions: {
      allowsAutomation: true,
      allowsCustomDomain: false,
      allowsForms: true,
      allowsIndexing: false,
      allowsNavigation: false,
      allowsSeo: false,
      allowsSiteShell: false,
      allowsTheming: true,
      supportsCourseProgress: false,
      supportsFunnelRouting: false,
      supportsVerification: false,
    },
    compatibility: {
      currentPageTypes: [],
      currentSiteTypes: [],
    },
  },
  {
    key: "lesson",
    label: "Lesson",
    description: "Course lesson output for learning content and exercises.",
    channel: "web",
    state: "active",
    capabilities: [
      "analytics",
      "automation",
      "conversion_tracking",
      "course_progress",
      "forms",
      "theming",
    ],
    restrictions: {
      allowsAutomation: true,
      allowsCustomDomain: false,
      allowsForms: true,
      allowsIndexing: false,
      allowsNavigation: true,
      allowsSeo: false,
      allowsSiteShell: true,
      allowsTheming: true,
      supportsCourseProgress: true,
      supportsFunnelRouting: false,
      supportsVerification: false,
    },
    compatibility: {
      currentPageTypes: ["course_lesson", "course_exercise"],
      currentSiteTypes: [],
      defaultPageType: "course_lesson",
    },
  },
  {
    key: "certificate",
    label: "Certificate",
    description: "Verification-oriented certificate output with constrained layout and no navigation assumptions.",
    channel: "web",
    state: "active",
    capabilities: [
      "analytics",
      "certificate_verification",
      "theming",
    ],
    restrictions: {
      allowsAutomation: false,
      allowsCustomDomain: false,
      allowsForms: false,
      allowsIndexing: false,
      allowsNavigation: false,
      allowsSeo: false,
      allowsSiteShell: false,
      allowsTheming: true,
      supportsCourseProgress: false,
      supportsFunnelRouting: false,
      supportsVerification: true,
    },
    compatibility: {
      currentPageTypes: ["certificate"],
      currentSiteTypes: [],
      defaultPageType: "certificate",
    },
  },
  {
    key: "display",
    label: "Display",
    description: "Display-mode output for kiosk, signage, and presentation surfaces. Explicit contract is defined ahead of runtime adoption.",
    channel: "display",
    state: "planned",
    capabilities: [
      "analytics",
      "theming",
    ],
    restrictions: {
      allowsAutomation: false,
      allowsCustomDomain: false,
      allowsForms: false,
      allowsIndexing: false,
      allowsNavigation: false,
      allowsSeo: false,
      allowsSiteShell: false,
      allowsTheming: true,
      supportsCourseProgress: false,
      supportsFunnelRouting: false,
      supportsVerification: false,
    },
    compatibility: {
      currentPageTypes: [],
      currentSiteTypes: [],
    },
  },
] as const satisfies readonly CanonicalOutputContract[];

const OUTPUT_CONTRACTS_BY_KEY = new Map<CanonicalOutputKey, CanonicalOutputContract>(
  OUTPUT_CONTRACTS.map((contract) => [contract.key, contract]),
);

const PAGE_TYPE_TO_OUTPUT_KEY = {
  lead_capture: "page",
  webinar: "page",
  sales: "page",
  booking: "page",
  thank_you: "page",
  link_in_bio: "page",
  custom: "page",
  site_page: "page",
  blog_post: "page",
  funnel_step: "funnel",
  course_lesson: "lesson",
  course_exercise: "lesson",
  certificate: "certificate",
} as const satisfies Record<PageType, CanonicalOutputKey>;

const SITE_TYPE_TO_OUTPUT_KEY = {
  website: "page",
  funnel: "funnel",
  blog: "page",
  link_in_bio: "page",
} as const satisfies Record<SiteType, CanonicalOutputKey>;

export const CANONICAL_OUTPUT_CONTRACTS = OUTPUT_CONTRACTS;
export const PAGE_TYPE_OUTPUT_MAP = PAGE_TYPE_TO_OUTPUT_KEY;
export const SITE_TYPE_OUTPUT_MAP = SITE_TYPE_TO_OUTPUT_KEY;

export function getCanonicalOutputContract(
  outputKey: CanonicalOutputKey,
): CanonicalOutputContract {
  return OUTPUT_CONTRACTS_BY_KEY.get(outputKey)!;
}

export function getOutputKeyForPageType(pageType: PageType): CanonicalOutputKey {
  return PAGE_TYPE_TO_OUTPUT_KEY[pageType];
}

export function getOutputKeyForSiteType(siteType: SiteType): CanonicalOutputKey {
  return SITE_TYPE_TO_OUTPUT_KEY[siteType];
}

export function isPageTypeCompatibleWithOutput(
  outputKey: CanonicalOutputKey,
  pageType: PageType,
): boolean {
  return getCanonicalOutputContract(outputKey).compatibility.currentPageTypes.includes(pageType);
}

export function isSiteTypeCompatibleWithOutput(
  outputKey: CanonicalOutputKey,
  siteType: SiteType,
): boolean {
  return getCanonicalOutputContract(outputKey).compatibility.currentSiteTypes.includes(siteType);
}

export function getDefaultPageTypeForOutput(
  outputKey: CanonicalOutputKey,
): PageType | undefined {
  return getCanonicalOutputContract(outputKey).compatibility.defaultPageType;
}

export function getDefaultSiteTypeForOutput(
  outputKey: CanonicalOutputKey,
): SiteType | undefined {
  return getCanonicalOutputContract(outputKey).compatibility.defaultSiteType;
}

export function resolveCanonicalOutputContract(
  input: ResolveOutputContractInput,
): ResolvedOutputContract {
  if (input.outputKey) {
    return {
      key: input.outputKey,
      source: "output_key",
      contract: getCanonicalOutputContract(input.outputKey),
    };
  }

  if (input.pageType) {
    const key = getOutputKeyForPageType(input.pageType);
    return {
      key,
      source: "page_type",
      contract: getCanonicalOutputContract(key),
    };
  }

  if (input.siteType) {
    const key = getOutputKeyForSiteType(input.siteType);
    return {
      key,
      source: "site_type",
      contract: getCanonicalOutputContract(key),
    };
  }

  return {
    key: "page",
    source: "default",
    contract: getCanonicalOutputContract("page"),
  };
}
